import React, { useContext, useState } from "react";

import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { deriveVideoUploadZoneState } from "./advertisingVideoState";
import { FormTranslationsContext } from "../../contexts/FormTranslationsContext";
import { useAdvertisingVideoUpload } from "../../hooks/useAdvertisingVideoUpload";
import VideoUploadZone from "../form/VideoUploadZone";
import ConfirmModal from "../modal/ConfirmModal";

import type { JSX } from "react";

import type { PageFieldArray } from "../../contexts/FormTranslationsContext";
import type {
  AdvertisingTranslationFormField,
  AdvertisingVideoByLanguageUuid,
} from "../../utils/translationAdapters/advertising";

interface AdvertisingVideoUploadProps {
  campaignUuid: string;
  videoByLanguageUuid: AdvertisingVideoByLanguageUuid;
  onDeleteVideo: (translationIndex: number) => Promise<void>;
  isDeletingVideo?: boolean;
}

export default function AdvertisingVideoUpload({
  campaignUuid,
  videoByLanguageUuid,
  onDeleteVideo,
  isDeletingVideo,
}: AdvertisingVideoUploadProps): JSX.Element | null {
  const { t } = useTranslation("editor-advertisings");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { activeLanguageIndex } = useContext(FormTranslationsContext);
  const methods =
    useFormContext<PageFieldArray<AdvertisingTranslationFormField>>();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const translationList = useWatch({
    control: methods.control,
    name: "translations",
  });

  const translation = translationList?.[activeLanguageIndex];
  const defaultTranslation = translationList?.find(
    (translationItem) => translationItem.default,
  );

  const languageUuid = translation?.language_uuid ?? "";

  const upload = useAdvertisingVideoUpload({
    campaignUuid,
    languageUuid,
    projectSlug,
  });

  if (!translation) return null;

  const languageName = t(`langs.${translation.language}`, { ns: "languages" });
  const defaultLanguageName = defaultTranslation
    ? t(`langs.${defaultTranslation.language}`, { ns: "languages" })
    : "";

  const defaultLanguageUuid = defaultTranslation?.language_uuid ?? "";

  const state = deriveVideoUploadZoneState({
    video: videoByLanguageUuid[languageUuid],
    defaultLanguageVideo: videoByLanguageUuid[defaultLanguageUuid],
    isDefaultLanguage: translation.default,
    job: upload.job,
    defaultLanguageName,
  });

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      await onDeleteVideo(activeLanguageIndex);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting advertising video:", error);
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="font-semibold">{t("videoUpload.title")}</div>
      <VideoUploadZone
        languageName={languageName}
        onDelete={(): void => setIsDeleteModalOpen(true)}
        onRetry={upload.onRetry}
        onSelectFile={upload.onSelectFile}
        state={state}
      />
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={(): void => setIsDeleteModalOpen(false)}
        title={t("videoUpload.deleteModal.title")}
        actions={[
          {
            label: t("videoUpload.deleteModal.confirm"),
            onClick: handleConfirmDelete,
            buttonVariant: "delete",
            isDisabled: isDeletingVideo,
          },
          {
            label: t("videoUpload.deleteModal.cancel"),
            onClick: (): void => setIsDeleteModalOpen(false),
          },
        ]}
      >
        <p>
          {t("videoUpload.deleteModal.body", {
            language: languageName,
            defaultLanguage: defaultLanguageName,
          })}
        </p>
      </ConfirmModal>
    </div>
  );
}
